import type { DocPageContent, PageMeta } from './types';

/** Support at /support: the listing's support contact. No response-time promise is made until Erin sets one. */
export const support: DocPageContent = {
  meta: {
    path: '/support',
    name: 'Pairing support',
    title: 'Support | Pairing by Denamico',
    description: 'How to get help with Pairing by Denamico: contact Denamico, what to include, and fixes for the most common HubSpot connection problems.',
    ogImage: '/og/client.png',
  } satisfies PageMeta,

  hero: {
    eyebrow: 'Support',
    headline: 'Get help with Pairing',
    subhead: 'Denamico builds and runs Pairing, so the people who answer you can see how your workspace is set up.',
  },

  sections: [
    {
      id: 'contact',
      headline: 'Contact Denamico',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Signed in to Pairing: open Support in the sidebar and choose New request. The page you came from and a summary of your workspace are attached for you, with no record values.',
            'Not signed in, or locked out: email support@denamico.com.',
          ],
        },
        { kind: 'links', items: [{ label: 'Email support@denamico.com', href: 'mailto:support@denamico.com' }] },
        {
          kind: 'p',
          text: 'Include your company name, the HubSpot portal ID, and what you expected to happen. Do not send passwords, API keys or customer records. If we need an example record, we will ask for its ID.',
        },
        {
          kind: 'p',
          text: 'Denamico replies in the request thread, or by email if you wrote to us. Requests show as Open, Waiting on you or Resolved.',
        },
      ],
    },
    {
      id: 'common',
      headline: 'Common problems',
      blocks: [
        {
          kind: 'table',
          caption: 'Common HubSpot connection problems and what to do',
          columns: ['What you see', 'What to do'],
          rows: [
            [
              'The connection shows Sign-in failed, or records are held with "Sign-in to a system failed"',
              'HubSpot stopped accepting Pairing\'s sign-in. This happens when the app was uninstalled, or a permission was removed. On Connections, choose Reconnect HubSpot, pick the same portal, and confirm. Then replay the held records.',
            ],
            [
              'That HubSpot portal is already connected to another Pairing workspace',
              'A HubSpot portal belongs to one workspace. Choose another portal, or contact Denamico.',
            ],
            [
              'You chose a different HubSpot portal from the one you were reconnecting',
              'Reconnect again and choose the same portal. To add another portal, use Connect a HubSpot portal.',
            ],
            [
              'The install link has stopped working',
              'Links expire after a short time and work once. Ask whoever sent it for a new one.',
            ],
            [
              'A feature is missing, such as Projects or imports',
              'The optional permission it needs was not granted, or your HubSpot plan does not include the object. Reconnect and approve the app again. The permissions are explained on the HubSpot app page.',
            ],
          ],
        },
      ],
    },
    {
      id: 'removing',
      headline: 'Uninstalling and removing your data',
      blocks: [
        {
          kind: 'p',
          text: 'What happens when you uninstall the app, and what stays in HubSpot, is on the HubSpot app page. To have your workspace\'s data removed from Pairing, contact support. It is done by hand today.',
        },
        {
          kind: 'links',
          items: [
            { label: 'HubSpot app: setup, permissions and uninstall', href: '/hubspot-app' },
          ],
        },
      ],
    },
    {
      id: 'security',
      headline: 'Reporting a security problem',
      blocks: [
        {
          kind: 'p',
          text: 'Email support@denamico.com with Security in the subject. Please do not include customer data in the report. How Pairing protects data is on the security page.',
        },
        { kind: 'links', items: [{ label: 'Security', href: '/security' }] },
      ],
    },
  ],
};
